/**
 * SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/KPIManagerReview.tsx
 * PURPOSE: Manager KPI review page — view pending reviews, adjust ratings, approve/reject
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-04-01
 * COMPLIANCE: SDLC 6.3.1 + Vietnamese Labor Law 2019 + NQH Compensation Framework V2.4
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management, KPI Manager Review & Approval
 * - Impact: Revenue Critical — Q2 bonus calculation, 3-Pillar Compensation Model
 * - Stakeholders: Branch Managers, HR Team, NQH Holding Finance
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Page Components
 * - Pattern: React Router, Page Layout, Component Composition
 * - Dependencies: React 18, React Router, TypeScript
 *
 * @cross-references
 * - Design: docs/02-design/05-business-logic/COMPENSATION-FRAMEWORK-DESIGN.md
 * - API: backend/app/api/v2/kpi.py (KPI Manager Review endpoints)
 * - Related: frontend/src/pages/LeaveRequestModern.tsx (pattern reference)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ShiftSwapModern.module.css';

/* ── Interfaces ─────────────────────────────────────────────────────────── */

interface KPIItem {
  id: string;
  name_vi: string;
  weight: number;
  employee_score: string;
  manager_score: string;
}

interface PendingReview {
  employee_id: string;
  employee_name: string;
  job_family: string;
  job_family_multiplier: number;
  base_salary: number;
  kpi_percentage: number;
  submitted_at: string;
  deadline: string;
  status: string;
}

interface EmployeeKPIDetail {
  employee_id: string;
  employee_name: string;
  job_family: string;
  job_family_multiplier: number;
  base_salary: number;
  kpi_percentage: number;
  kpis: KPIItem[];
}

/* ── Constants ──────────────────────────────────────────────────────────── */

const RATING_SCALE: { value: string; label: string; multiplier: number }[] = [
  { value: 'FEE', label: 'FEE — Vượt trội', multiplier: 1.5 },
  { value: 'EE', label: 'EE — Xuất sắc', multiplier: 1.2 },
  { value: 'ME', label: 'ME — Đạt yêu cầu', multiplier: 1.0 },
  { value: 'PME', label: 'PME — Cần cải thiện', multiplier: 0.7 },
  { value: 'DNME', label: 'DNME — Không đạt', multiplier: 0.0 },
];

const RATING_MULTIPLIER: Record<string, number> = {
  FEE: 1.5,
  EE: 1.2,
  ME: 1.0,
  PME: 0.7,
  DNME: 0.0,
};

const RATING_LABEL: Record<string, string> = {
  FEE: 'Vượt trội',
  EE: 'Xuất sắc',
  ME: 'Đạt yêu cầu',
  PME: 'Cần cải thiện',
  DNME: 'Không đạt',
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Chờ duyệt',
  APPROVED: 'Đã duyệt',
  REJECTED: 'Từ chối',
};

/* ── Helpers ────────────────────────────────────────────────────────────── */

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const token = localStorage.getItem('access_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const raw = localStorage.getItem('auth-storage');
    if (raw) {
      const parsed = JSON.parse(raw);
      const tenantKey = parsed?.state?.tenantKey || parsed?.state?.tenant_key;
      if (tenantKey) headers['X-Tenant-Key'] = tenantKey;
    }
  } catch { /* ignore */ }
  return headers;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('vi-VN');
  } catch {
    return iso;
  }
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);
}

/**
 * Calculate weighted KPI multiplier from individual manager scores.
 * Each KPI has a weight (0-1) and a rating (FEE/EE/ME/PME/DNME).
 * Weighted multiplier = SUM(weight_i * rating_multiplier_i)
 */
function calculateWeightedMultiplier(kpis: KPIItem[], managerScores: Record<string, string>): number {
  let totalWeight = 0;
  let weightedSum = 0;
  for (const kpi of kpis) {
    const score = managerScores[kpi.id] || kpi.manager_score || kpi.employee_score;
    const mult = RATING_MULTIPLIER[score] ?? 1.0;
    weightedSum += kpi.weight * mult;
    totalWeight += kpi.weight;
  }
  if (totalWeight === 0) return 1.0;
  return weightedSum / totalWeight;
}

/**
 * Q2 Bonus = Base × KPI% × KPI_Multiplier × JF_Multiplier
 */
function calculateQ2Bonus(
  baseSalary: number,
  kpiPercentage: number,
  kpiMultiplier: number,
  jfMultiplier: number,
): number {
  return baseSalary * (kpiPercentage / 100) * kpiMultiplier * jfMultiplier;
}

/* ── Component ──────────────────────────────────────────────────────────── */

export const KPIManagerReview: React.FC = () => {
  const navigate = useNavigate();

  // Pending reviews list
  const [reviews, setReviews] = useState<PendingReview[]>([]);
  const [loading, setLoading] = useState(true);

  // Expanded employee detail
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<EmployeeKPIDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Manager adjustments for the expanded employee
  const [managerScores, setManagerScores] = useState<Record<string, string>>({});
  const [managerNotes, setManagerNotes] = useState('');

  // Action state
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /* ── Load pending reviews ─────────────────────────────────────────────── */

  const loadPendingReviews = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/v2/kpi/manager/pending', {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const items = data.items || data.reviews || data.data || [];
        setReviews(Array.isArray(items) ? items : []);
      } else {
        setReviews([]);
      }
    } catch {
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPendingReviews();
  }, [loadPendingReviews]);

  /* ── Load employee KPI detail ─────────────────────────────────────────── */

  const loadEmployeeDetail = useCallback(async (employeeId: string) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await fetch(`/api/v2/kpi/employee/${employeeId}/current`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data: EmployeeKPIDetail = await res.json();
        setDetail(data);
        // Initialize manager scores from existing manager_score or fallback to employee_score
        const initialScores: Record<string, string> = {};
        for (const kpi of data.kpis || []) {
          initialScores[kpi.id] = kpi.manager_score || kpi.employee_score;
        }
        setManagerScores(initialScores);
        setManagerNotes('');
      } else {
        const data = await res.json().catch(() => ({}));
        const errMsg = data?.detail || data?.message_vi || 'Không thể tải chi tiết KPI.';
        setError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
      }
    } catch {
      setError('Lỗi kết nối. Vui lòng thử lại.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleToggleExpand = useCallback((employeeId: string) => {
    if (expandedId === employeeId) {
      setExpandedId(null);
      setDetail(null);
      setManagerScores({});
      setManagerNotes('');
    } else {
      setExpandedId(employeeId);
      setError(null);
      setMessage(null);
      loadEmployeeDetail(employeeId);
    }
  }, [expandedId, loadEmployeeDetail]);

  /* ── Manager score change ─────────────────────────────────────────────── */

  const handleScoreChange = useCallback((kpiId: string, value: string) => {
    setManagerScores(prev => ({ ...prev, [kpiId]: value }));
  }, []);

  /* ── Approve / Reject ─────────────────────────────────────────────────── */

  const handleAction = useCallback(async (employeeId: string, action: 'approve' | 'reject') => {
    setSubmitting(true);
    setError(null);
    setMessage(null);

    try {
      const body = {
        action,
        manager_scores: managerScores,
        notes: managerNotes || undefined,
      };

      const res = await fetch(`/api/v2/kpi/employee/${employeeId}/approve`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        const actionLabel = action === 'approve' ? 'phê duyệt' : 'từ chối';
        setMessage(`Đánh giá KPI đã được ${actionLabel} thành công.`);
        // Remove from pending list
        setReviews(prev => prev.filter(r => r.employee_id !== employeeId));
        setExpandedId(null);
        setDetail(null);
        setManagerScores({});
        setManagerNotes('');
      } else {
        const rawDetail = data?.detail;
        const errMsg =
          (typeof rawDetail === 'string' ? rawDetail : rawDetail?.message_vi || rawDetail?.message) ||
          data?.message_vi ||
          data?.message ||
          'Không thể xử lý yêu cầu.';
        setError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
      }
    } catch {
      setError('Lỗi kết nối. Vui lòng thử lại.');
    } finally {
      setSubmitting(false);
    }
  }, [managerScores, managerNotes]);

  /* ── Bonus preview calculation ────────────────────────────────────────── */

  const bonusPreview = detail
    ? (() => {
        const kpiMult = calculateWeightedMultiplier(detail.kpis || [], managerScores);
        const q2 = calculateQ2Bonus(
          detail.base_salary,
          detail.kpi_percentage,
          kpiMult,
          detail.job_family_multiplier,
        );
        return { kpiMultiplier: kpiMult, q2Bonus: q2 };
      })()
    : null;

  /* ── Render ───────────────────────────────────────────────────────────── */

  const pendingCount = reviews.filter(r => r.status === 'PENDING').length;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <h1 id="kpi-review-title" className={styles.title}>
            KPI Duyệt Đánh Giá
            {pendingCount > 0 && (
              <span
                style={{
                  marginLeft: 12,
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  background: '#fef9c3',
                  color: '#854d0e',
                  padding: '4px 12px',
                  borderRadius: 20,
                }}
                data-testid="pending-count-badge"
              >
                {pendingCount} đang chờ duyệt
              </span>
            )}
          </h1>
          <button onClick={() => navigate('/dashboard')} className={styles.backButton}>
            Về Dashboard
          </button>
        </div>
      </header>

      <main aria-labelledby="kpi-review-title" className={styles.main}>

        {/* Status messages */}
        {message && (
          <p
            className={styles.statusSuccess}
            data-testid="kpi-success-msg"
            role="alert"
            aria-live="polite"
          >
            {message}
          </p>
        )}
        {error && (
          <p className={styles.statusError} data-testid="kpi-error-msg">
            {error}
          </p>
        )}

        {/* Pending reviews list */}
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Danh sách đánh giá chờ duyệt</h2>

          {loading ? (
            <p className={styles.cardDescription}>Đang tải...</p>
          ) : reviews.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }} data-testid="empty-state">
              <span style={{ fontSize: '2rem' }}>&#10003;</span>
              <p className={styles.cardDescription} style={{ marginTop: 8 }}>
                Không có đánh giá nào chờ duyệt
              </p>
            </div>
          ) : (
            <ul className={styles.requestList}>
              {reviews.map(review => (
                <li
                  key={review.employee_id}
                  className={styles.requestItem}
                  data-testid={`review-${review.employee_id}`}
                >
                  {/* Review summary row */}
                  <div
                    className={styles.requestHeader}
                    onClick={() => handleToggleExpand(review.employee_id)}
                    style={{ cursor: 'pointer' }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleToggleExpand(review.employee_id); }}
                    data-testid={`expand-${review.employee_id}`}
                  >
                    <span
                      className={styles.statusBadge}
                      data-status={review.status}
                    >
                      {STATUS_LABELS[review.status] || review.status}
                    </span>
                    <span className={styles.requestDate}>
                      Hạn: {formatDate(review.deadline)}
                    </span>
                  </div>
                  <p className={styles.requestReason}>
                    <strong>{review.employee_name}</strong> — {review.job_family}
                  </p>
                  <p className={styles.requestPeer}>
                    Ngày gửi: {formatDate(review.submitted_at)}
                  </p>

                  {/* Expanded detail section */}
                  {expandedId === review.employee_id && (
                    <div
                      style={{
                        marginTop: 16,
                        borderTop: '1px solid #e5e7eb',
                        paddingTop: 16,
                      }}
                      data-testid={`detail-${review.employee_id}`}
                    >
                      {detailLoading ? (
                        <p className={styles.cardDescription}>Đang tải chi tiết KPI...</p>
                      ) : detail ? (
                        <>
                          {/* KPI items table */}
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                              <thead>
                                <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                                  <th style={{ padding: '8px 12px', color: '#374151', fontWeight: 600 }}>Chỉ tiêu KPI</th>
                                  <th style={{ padding: '8px 12px', color: '#374151', fontWeight: 600, width: 80, textAlign: 'center' }}>Trọng số</th>
                                  <th style={{ padding: '8px 12px', color: '#374151', fontWeight: 600, textAlign: 'center' }}>NV tự đánh giá</th>
                                  <th style={{ padding: '8px 12px', color: '#374151', fontWeight: 600, textAlign: 'center' }}>Quản lý đánh giá</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(detail.kpis || []).map(kpi => (
                                  <tr key={kpi.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                    <td style={{ padding: '10px 12px', color: '#111827' }}>
                                      {kpi.name_vi}
                                    </td>
                                    <td style={{ padding: '10px 12px', textAlign: 'center', color: '#6b7280' }}>
                                      {(kpi.weight * 100).toFixed(0)}%
                                    </td>
                                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                      <span
                                        className={styles.statusBadge}
                                        data-status={kpi.employee_score === 'FEE' || kpi.employee_score === 'EE' ? 'APPROVED' : kpi.employee_score === 'DNME' ? 'REJECTED' : 'PENDING'}
                                      >
                                        {kpi.employee_score} — {RATING_LABEL[kpi.employee_score] || kpi.employee_score}
                                      </span>
                                    </td>
                                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                      <select
                                        value={managerScores[kpi.id] || kpi.employee_score}
                                        onChange={e => handleScoreChange(kpi.id, e.target.value)}
                                        className={styles.input}
                                        style={{ minWidth: 180, padding: '6px 10px' }}
                                        data-testid={`score-select-${kpi.id}`}
                                      >
                                        {RATING_SCALE.map(r => (
                                          <option key={r.value} value={r.value}>{r.label}</option>
                                        ))}
                                      </select>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* Manager notes */}
                          <div className={styles.formGroup} style={{ marginTop: 16 }}>
                            <label htmlFor={`notes-${review.employee_id}`} className={styles.label}>
                              Ghi chú quản lý
                            </label>
                            <textarea
                              id={`notes-${review.employee_id}`}
                              value={managerNotes}
                              onChange={e => setManagerNotes(e.target.value)}
                              placeholder="Nhận xét, ghi chú cho nhân viên..."
                              maxLength={2000}
                              className={styles.textarea}
                              rows={3}
                              data-testid="manager-notes"
                            />
                          </div>

                          {/* Bonus preview */}
                          {bonusPreview && (
                            <div
                              style={{
                                marginTop: 16,
                                padding: '12px 16px',
                                background: '#eff6ff',
                                border: '1px solid #bfdbfe',
                                borderRadius: 8,
                              }}
                              data-testid="bonus-preview"
                            >
                              <p style={{ margin: 0, fontSize: '0.9rem', color: '#1e40af', fontWeight: 600 }}>
                                Dự tính thưởng Q2
                              </p>
                              <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#1e40af' }}>
                                Công thức: Lương cơ bản ({formatCurrency(detail.base_salary)})
                                {' '}x KPI% ({detail.kpi_percentage}%)
                                {' '}x Hệ số KPI ({bonusPreview.kpiMultiplier.toFixed(2)})
                                {' '}x Hệ số nhóm nghề ({detail.job_family_multiplier}x)
                              </p>
                              <p style={{ margin: '8px 0 0', fontSize: '1.1rem', color: '#1e3a8a', fontWeight: 700 }}>
                                = {formatCurrency(bonusPreview.q2Bonus)}
                              </p>
                            </div>
                          )}

                          {/* Action buttons */}
                          <div className={styles.formActions} style={{ marginTop: 16 }}>
                            <button
                              onClick={() => handleAction(review.employee_id, 'approve')}
                              disabled={submitting}
                              className={styles.primaryButton}
                              style={{ background: '#16a34a' }}
                              data-testid={`approve-${review.employee_id}`}
                            >
                              {submitting ? 'Đang xử lý...' : 'Phê duyệt'}
                            </button>
                            <button
                              onClick={() => handleAction(review.employee_id, 'reject')}
                              disabled={submitting}
                              className={styles.cancelButton}
                              style={{
                                padding: '10px 20px',
                                fontSize: '0.95rem',
                                fontWeight: 600,
                              }}
                              data-testid={`reject-${review.employee_id}`}
                            >
                              {submitting ? 'Đang xử lý...' : 'Từ chối'}
                            </button>
                          </div>
                        </>
                      ) : (
                        <p className={styles.cardDescription}>Không thể tải chi tiết.</p>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

      </main>
    </div>
  );
};

export default KPIManagerReview;
